(() => {
  const elementName = 'balour-motion-archive';

  if (customElements.get(elementName)) return;

  class BalourMotionArchive extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedAndReady) return;

      this.viewport = this.querySelector('[data-bma-viewport]');
      this.track = this.querySelector('[data-bma-track]');
      this.controls = this.querySelector('[data-bma-controls]');
      this.previousButton = this.querySelector('[data-bma-previous]');
      this.nextButton = this.querySelector('[data-bma-next]');
      this.currentLabel = this.querySelector('[data-bma-current]');
      this.progress = this.querySelector('[data-bma-progress]');
      this.status = this.querySelector('[data-bma-status]');

      if (!this.viewport || !this.track) return;

      this.isConnectedAndReady = true;
      this.activeIndex = 0;
      this.scrollFrame = null;
      this.programmaticTargetIndex = null;
      this.programmaticTimer = null;
      this.autoplayTimer = null;
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplayDelay = Math.max(Number.parseInt(this.dataset.autoplayDelay, 10) || 5000, 3000);
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.isHovered = false;
      this.hasFocus = false;
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.viewport.addEventListener('scroll', () => this.requestMeasure(), { passive: true, signal });
      this.viewport.addEventListener('pointerdown', () => this.handleUserInteraction(), { passive: true, signal });
      this.viewport.addEventListener('wheel', () => this.handleUserInteraction(), { passive: true, signal });
      this.viewport.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
      this.previousButton?.addEventListener('click', () => this.navigateTo(this.activeIndex - 1, true), { signal });
      this.nextButton?.addEventListener('click', () => this.navigateTo(this.activeIndex + 1, true), { signal });
      this.addEventListener('mouseenter', () => this.setHoverState(true), { signal });
      this.addEventListener('mouseleave', () => this.setHoverState(false), { signal });
      this.addEventListener('focusin', () => this.setFocusState(true), { signal });
      this.addEventListener('focusout', () => requestAnimationFrame(() => this.setFocusState(this.contains(document.activeElement))), { signal });
      document.addEventListener('visibilitychange', () => this.handleVisibilityChange(), { signal });
      this.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), { signal });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.requestMeasure());
        this.resizeObserver.observe(this.viewport);
      }

      if ('MutationObserver' in window) {
        this.mutationObserver = new MutationObserver(() => this.refresh());
        this.mutationObserver.observe(this.track, { childList: true });
      }

      this.refresh();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();

      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.programmaticTimer) clearTimeout(this.programmaticTimer);
      this.clearAutoplay();

      this.isConnectedAndReady = false;
    }

    refresh() {
      this.items = Array.from(this.querySelectorAll('[data-bma-item]'));
      const isCarousel = this.items.length > 1;

      this.viewport.tabIndex = isCarousel ? 0 : -1;
      if (isCarousel) {
        this.viewport.setAttribute('aria-roledescription', 'carousel');
      } else {
        this.viewport.removeAttribute('aria-roledescription');
      }

      this.items.forEach((item, index) => {
        item.setAttribute('aria-roledescription', 'slide');
        item.setAttribute('aria-label', `Photo ${index + 1} of ${this.items.length}`);
      });

      const selectedIndex = Math.min(this.activeIndex, Math.max(this.items.length - 1, 0));
      this.setActive(selectedIndex, false);

      if (!this.hasSetInitialPosition && this.items.length) {
        this.hasSetInitialPosition = true;
        this.isPositioning = true;

        requestAnimationFrame(() => {
          this.updateGutters();
          this.centerItem(this.items[0], 'auto');
          this.isPositioning = false;
          this.setActive(0, false);
          this.requestMeasure();
          this.queueAutoplay();
        });
        return;
      }

      this.requestMeasure();
      this.queueAutoplay();
    }

    requestMeasure() {
      if (this.scrollFrame) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.measure();
      });
    }

    measure() {
      if (this.isPositioning) return;

      if (!this.items.length) {
        if (this.controls) this.controls.hidden = true;
        if (this.progress) this.progress.style.transform = 'scaleX(0)';
        return;
      }

      this.updateGutters();

      if (this.programmaticTargetIndex !== null) {
        this.setActive(this.programmaticTargetIndex, false);

        if (this.controls) {
          const overflows = this.viewport.scrollWidth > this.viewport.clientWidth + 2;
          this.controls.hidden = this.items.length < 2 || !overflows;
        }

        return;
      }

      const viewportRect = this.viewport.getBoundingClientRect();
      const viewportCenter = viewportRect.left + viewportRect.width / 2;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.items.forEach((item, index) => {
        const itemRect = item.getBoundingClientRect();
        const itemCenter = itemRect.left + itemRect.width / 2;
        const distance = Math.abs(viewportCenter - itemCenter);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      this.setActive(nearestIndex, false);

      if (this.controls) {
        const overflows = this.viewport.scrollWidth > this.viewport.clientWidth + 2;
        this.controls.hidden = this.items.length < 2 || !overflows;
      }
    }

    updateGutters() {
      if (!this.items.length) return;

      const trackStyles = getComputedStyle(this.track);
      const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
      const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
      const minimum = rootFontSize * 2.2;
      const viewportHalf = this.viewport.clientWidth / 2;
      const firstHalf = this.items[0].offsetWidth / 2;
      const lastHalf = this.items[this.items.length - 1].offsetWidth / 2;
      const startGutter = Math.max(minimum, viewportHalf - firstHalf - gap);
      const endGutter = Math.max(minimum, viewportHalf - lastHalf - gap);

      this.track.style.setProperty('--bma-start-gutter', `${startGutter}px`);
      this.track.style.setProperty('--bma-end-gutter', `${endGutter}px`);
    }

    setActive(index, announce) {
      if (!this.items.length) {
        this.activeIndex = 0;
        if (this.currentLabel) this.currentLabel.textContent = '00';
        return;
      }

      const safeIndex = Math.max(0, Math.min(index, this.items.length - 1));
      this.activeIndex = safeIndex;

      this.items.forEach((item, itemIndex) => {
        item.classList.toggle('is-active', itemIndex === safeIndex);
      });

      if (this.currentLabel) {
        this.currentLabel.textContent = String(safeIndex + 1).padStart(2, '0');
      }

      if (this.progress) {
        const ratio = (safeIndex + 1) / this.items.length;
        this.progress.style.transform = `scaleX(${ratio})`;
      }

      if (this.previousButton) this.previousButton.disabled = safeIndex === 0;
      if (this.nextButton) this.nextButton.disabled = safeIndex === this.items.length - 1;

      if (announce && this.status) {
        this.status.textContent = `Photo ${safeIndex + 1} of ${this.items.length}`;
      }
    }

    goTo(index, announce = false) {
      if (!this.items.length) return;

      const safeIndex = Math.max(0, Math.min(index, this.items.length - 1));
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = reduceMotion ? 'auto' : 'smooth';

      this.cancelProgrammaticScroll(false);
      this.programmaticTargetIndex = safeIndex;

      this.setActive(safeIndex, announce);
      this.centerItem(this.items[safeIndex], behavior);

      if (behavior === 'auto') {
        this.programmaticTargetIndex = null;
        this.requestMeasure();
        return;
      }

      this.programmaticTimer = window.setTimeout(() => {
        this.programmaticTimer = null;
        this.programmaticTargetIndex = null;
        this.requestMeasure();
      }, 900);
    }

    navigateTo(index, announce = false) {
      this.goTo(index, announce);
      this.queueAutoplay();
    }

    handleUserInteraction() {
      this.cancelProgrammaticScroll();
      this.queueAutoplay();
    }

    clearAutoplay() {
      if (!this.autoplayTimer) return;
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    queueAutoplay() {
      this.clearAutoplay();

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const isPaused = this.pauseOnHover && (this.isHovered || this.hasFocus);
      if (!this.autoplayEnabled || reduceMotion || isPaused || document.hidden || this.items?.length < 2) return;

      this.autoplayTimer = window.setTimeout(() => {
        this.autoplayTimer = null;
        const nextIndex = this.activeIndex >= this.items.length - 1 ? 0 : this.activeIndex + 1;
        this.goTo(nextIndex, false);
        this.queueAutoplay();
      }, this.autoplayDelay);
    }

    setHoverState(isHovered) {
      this.isHovered = isHovered;
      this.queueAutoplay();
    }

    setFocusState(hasFocus) {
      this.hasFocus = hasFocus;
      this.queueAutoplay();
    }

    handleVisibilityChange() {
      this.queueAutoplay();
    }

    cancelProgrammaticScroll(measure = true) {
      if (this.programmaticTimer) {
        clearTimeout(this.programmaticTimer);
        this.programmaticTimer = null;
      }

      this.programmaticTargetIndex = null;
      if (measure) this.requestMeasure();
    }

    centerItem(item, behavior) {
      if (!item) return;

      const viewportRect = this.viewport.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const offset = itemRect.left + itemRect.width / 2 - (viewportRect.left + viewportRect.width / 2);

      if (Math.abs(offset) < 1) return;

      if (behavior === 'auto') {
        const previousBehavior = this.viewport.style.scrollBehavior;
        this.viewport.style.scrollBehavior = 'auto';
        this.viewport.scrollLeft += offset;
        this.viewport.style.scrollBehavior = previousBehavior;
        return;
      }

      this.viewport.scrollBy({ left: offset, behavior });
    }

    handleKeydown(event) {
      if (event.target !== this.viewport || !this.items.length) return;

      const isRtl = getComputedStyle(this).direction === 'rtl';
      let targetIndex = null;

      if (event.key === 'ArrowRight') targetIndex = this.activeIndex + (isRtl ? -1 : 1);
      if (event.key === 'ArrowLeft') targetIndex = this.activeIndex + (isRtl ? 1 : -1);
      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = this.items.length - 1;

      if (targetIndex === null) return;

      event.preventDefault();
      this.navigateTo(targetIndex, true);
    }

    handleBlockSelect(event) {
      const selectedItem = event.target.closest?.('[data-bma-item]')
        || this.items.find((item) => item.dataset.blockId === event.detail?.blockId);

      if (!selectedItem || !this.contains(selectedItem)) return;

      const selectedIndex = this.items.indexOf(selectedItem);
      if (selectedIndex >= 0) {
        this.goTo(selectedIndex, false);
        this.clearAutoplay();
      }
    }
  }

  customElements.define(elementName, BalourMotionArchive);
})();
